import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAgentMap, useCreateGeeJob } from './api';
import type { LandCategory, MapParcel } from './types';

interface RunFarmInfrastructureScanOptions {
  parcels: MapParcel[];
  entityId?: string | null;
  landCategory?: LandCategory | null;
  declaredTypes?: string[];
  replaceExisting?: boolean;
}

export function useFarmInfrastructureScan() {
  const agentMap = useAgentMap();
  const createGeeJob = useCreateGeeJob();
  const qc = useQueryClient();

  const runScan = useCallback(async ({
    parcels,
    entityId,
    landCategory,
    declaredTypes = [],
    replaceExisting = true,
  }: RunFarmInfrastructureScanOptions) => {
    const targets = Array.from(new Map(
      (parcels || [])
        .filter((parcel): parcel is MapParcel => Boolean(parcel?.id && parcel.geometry))
        .map((parcel) => [parcel.id, parcel])
    ).values());

    let firstJobId: string | null = null;
    const runs: Array<{
      parcel_id: string;
      agent_job_id: string | null;
      gee_job_id: string | null;
      status: 'started' | 'failed';
      error: string | null;
    }> = [];

    for (const target of targets) {
      const [agentResult, geeResult] = await Promise.allSettled([
        agentMap.mutateAsync({
          parcel_id: target.id,
          entity_id: entityId || target.linked_entity_id || undefined,
          mode: 'detect_infrastructure',
          replace_existing: replaceExisting,
          land_category: (target.land_category as LandCategory | null) || landCategory || undefined,
          declared_types: declaredTypes,
        }),
        createGeeJob.mutateAsync({
          job_type: 'land_cover',
          parcel_id: target.id,
          geometry: target.geometry,
          params: {
            source: 'farm_checklist_scan',
            replace_existing: replaceExisting,
            include_boundary_fence: true,
            active_template_ids: target.active_template_ids || [],
          },
        }),
      ]);

      const nextJobId = agentResult.status === 'fulfilled'
        ? (agentResult.value.job_id || agentResult.value.pipeline_id)
        : (geeResult.status === 'fulfilled' ? geeResult.value.job_id : null);
      if (!firstJobId && nextJobId) {
        firstJobId = nextJobId;
      }

      runs.push({
        parcel_id: target.id,
        agent_job_id: agentResult.status === 'fulfilled' ? (agentResult.value.job_id || agentResult.value.pipeline_id) : null,
        gee_job_id: geeResult.status === 'fulfilled' ? geeResult.value.job_id : null,
        status: (nextJobId || geeResult.status === 'fulfilled') ? 'started' : 'failed',
        error: agentResult.status === 'rejected'
          ? String(agentResult.reason)
          : geeResult.status === 'rejected'
            ? String(geeResult.reason)
            : null,
      });

      await qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure', 'within', target.id] });
    }

    await qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
    await qc.invalidateQueries({ queryKey: ['mapper', 'suggestions'] });
    await qc.invalidateQueries({ queryKey: ['mapper', 'suggestion-metrics'] });

    return {
      firstJobId,
      runs,
      targets,
    };
  }, [agentMap, createGeeJob, qc]);

  return {
    runScan,
    isPending: agentMap.isPending || createGeeJob.isPending,
  };
}
